import React, { createContext, useContext, useState } from 'react';
import { PatientInfo, AppointmentSlot } from './types';

interface HVAHealthCareContextType {
  // Patient information
  patientInfo: PatientInfo | null;
  setPatientInfo: (info: PatientInfo | null) => void;

  // Validation state
  isAmkaValidated: boolean;
  setIsAmkaValidated: (validated: boolean) => void;

  // Conversation state
  conversationStage:
    | 'identification'
    | 'service_selection'
    | 'scheduling'
    | 'confirmation'
    | 'finished';
  setConversationStage: (
    stage:
      | 'identification'
      | 'service_selection'
      | 'scheduling'
      | 'confirmation'
      | 'finished'
  ) => void;

  // Current selections in the conversation
  selectedService: string | null; // ID of the service (doctor or examination)
  setSelectedService: (serviceId: string | null) => void;

  selectedLocation: string | null; // ID of location
  setSelectedLocation: (locationId: string | null) => void;

  selectedDoctor: string | null; // ID of doctor
  setSelectedDoctor: (doctorId: string | null) => void;

  selectedExamination: string | null; // ID of examination
  setSelectedExamination: (examinationId: string | null) => void;

  // Slots and booking
  availableSlots: AppointmentSlot[];
  setAvailableSlots: (slots: AppointmentSlot[]) => void;

  bookedAppointment: AppointmentSlot | null;
  setBookedAppointment: (appointment: AppointmentSlot | null) => void;

  // System feedback messages
  systemMessages: {
    id: string;
    type: string;
    message: string;
    details?: string;
    timestamp: Date;
  }[];
  addSystemMessage: (type: string, message: string, details?: string) => void;
}

const HVAHealthCareContext = createContext<
  HVAHealthCareContextType | undefined
>(undefined);

export const HVAHealthCareProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Basic state
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  const [isAmkaValidated, setIsAmkaValidated] = useState(true);

  // Conversation stage
  const [conversationStage, setConversationStage] = useState<
    | 'identification'
    | 'service_selection'
    | 'scheduling'
    | 'confirmation'
    | 'finished'
  >('service_selection');

  // Selections during conversation
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<string | null>(null);
  const [selectedExamination, setSelectedExamination] = useState<string | null>(
    null
  );

  // Slots and appointments
  const [availableSlots, setAvailableSlots] = useState<AppointmentSlot[]>([]);
  const [bookedAppointment, setBookedAppointment] =
    useState<AppointmentSlot | null>(null);

  // System messages
  const [systemMessages, setSystemMessages] = useState<
    {
      id: string;
      type: string;
      message: string;
      details?: string;
      timestamp: Date;
    }[]
  >([]);

  // Function to add system messages
  const addSystemMessage = (
    type: string,
    message: string,
    details?: string
  ) => {
    setSystemMessages(prev => [
      {
        id: Math.random().toString(36).substring(2, 11),
        type,
        message,
        details,
        timestamp: new Date()
      },
      ...prev
    ]);
  };

  const value = {
    patientInfo,
    setPatientInfo,
    isAmkaValidated,
    setIsAmkaValidated,
    conversationStage,
    setConversationStage,
    selectedService,
    setSelectedService,
    selectedLocation,
    setSelectedLocation,
    selectedDoctor,
    setSelectedDoctor,
    selectedExamination,
    setSelectedExamination,
    availableSlots,
    setAvailableSlots,
    bookedAppointment,
    setBookedAppointment,
    systemMessages,
    addSystemMessage
  };

  return (
    <HVAHealthCareContext.Provider value={value}>
      {children}
    </HVAHealthCareContext.Provider>
  );
};

export const useHVAHealthCare = () => {
  const context = useContext(HVAHealthCareContext);
  if (context === undefined) {
    throw new Error(
      'useHVAHealthCare must be used within a HVAHealthCareProvider'
    );
  }
  return context;
};
